import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {PatternShift} from '@domain/calendar/pattern/pattern-shift';
import {Schedule} from '@domain/calendar/schedule/schedule';
import {ScheduleException} from '@domain/calendar/schedule/schedule-exception';
import {SchedulePattern} from '@domain/calendar/schedule/schedule-pattern';
import {SchedulePreview} from '@domain/calendar/schedule/schedule-preview';
import {ScheduleUnavailableDay} from '@domain/calendar/schedule/schedule-unavailable-day';
import {HttpSchedulesService} from '@infrastructure/http/calendar/schedule/http-schedules.service';
import {CalendarTimelineService} from '@presentation/pages/calendar/calendar-timeline.service';
import {
  AssertionUtils,
  BaseComponent,
  CellButtonComponent,
  CellButtonParams,
  ColDefBuilderFactoryService,
  CrudOverviewButtonConfig,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DateTimeFormat,
  DateTimePipe,
  EmptyCellRendererComponent,
  GridOptionsBuilder,
  LoadingCellOverlayComponent,
  NoDataOverlayComponentParams,
  OverlayActionsService,
  OverlayComponentParamsAction,
  ScheduleItem,
  TranslateService
} from '@vdw/angular-component-library';
import {
  CellRendererSelectorResult,
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IHeaderParams,
  IRowNode,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITooltipParams,
  RowClassParams,
  ValueGetterParams
} from 'ag-grid-community';
import {DateTime, Duration} from 'luxon';
import {takeUntil} from 'rxjs';
import {JumpToDatePickerHeaderParams} from './jump-to-date-picker-header/jump-to-date-picker-header-params.interface';
import {JumpToDatePickerHeaderComponent} from './jump-to-date-picker-header/jump-to-date-picker-header.component';
import {SchedulePreviewRowData} from './schedule-preview-row-data.interface';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html'
})
export class ScheduleComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public patterns: SchedulePattern[] = [];
  @Input() public unavailableDays: ScheduleUnavailableDay[] = [];
  @Input() public exceptions: ScheduleException[] = [];
  @Input() public isLoadingSchedule: boolean;
  @Output() public addPattern = new EventEmitter<void>();

  public config: CrudOverviewDataConfig<SchedulePreviewRowData>;
  public loadingForTheFirstTime = true;
  private schedulePreviews: SchedulePreview[] = [];
  private gridApi: GridApi;
  private selectedDate: DateTime;
  private readonly TODAY = DateTime.now().startOf('day');
  private readonly DATE_COLUMN_WIDTH = 90;
  private readonly CELL_BUTTON_COLUMN_WIDTH = 32;
  private readonly SLICE_SIZE = 30;
  private readonly PAST_DAYS_TO_SHOW = 3;
  private readonly PREVIEW_START_DATE = this.TODAY.minus({days: this.PAST_DAYS_TO_SHOW});
  private readonly PATTERN_TRANSLATION_KEY = NavigationUtils.getNavigationData(NavigationId.PATTERN_MODULE).navigationKey;
  private readonly NO_OVERLAY_ACTIONS: OverlayComponentParamsAction[] = [
    {
      key: 'addPattern',
      titleKey: this.translate.instant('GENERAL.ACTIONS.ADD_OBJECT', {object: this.translate.instant(this.PATTERN_TRANSLATION_KEY, {count: 1}).toLowerCase()}),
      isPrimary: true
    }
  ];

  public constructor(
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<SchedulePreviewRowData>,
    private readonly calendarTimelineService: CalendarTimelineService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly dateTimePipe: DateTimePipe,
    private readonly overlayActionsService: OverlayActionsService,
    private readonly schedules: HttpSchedulesService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.selectedDate = this.PREVIEW_START_DATE;
    this.subscribeToOverlayActions();

    this.config = this.builderFactoryService
      .getBuilder()
      .withGridOptions(this.getColumnDefs(), GridIdentifier.SCHEDULE, [], (builder: GridOptionsBuilder) => {
        builder
          .withoutGetRowId()
          .withoutRowSelection()
          .withRowClassRules({
            ['today-marker']: (params: RowClassParams) => params.data?.date?.hasSame(this.TODAY, 'day'),
            ['past-day']: (params: RowClassParams) => params.data?.date < this.TODAY,
            ['weekend']: (params: RowClassParams) => [6, 7].includes(params.data?.date?.weekday),
            ['non-working-day']: (params: RowClassParams) => !params.data?.isWorkingDay
          })
          .withLoadingCellRenderer('customLoadingCellRenderer')
          .withComponents({customLoadingCellRenderer: LoadingCellOverlayComponent})
          .withNoRowsOverlay({
            scale: 0.7,
            titleParam: this.translate.instant(this.PATTERN_TRANSLATION_KEY, {count: 2}),
            hideDescription: false,
            descriptionKey: this.translate.instant('GENERAL.ACTIONS.SELECT_OBJECT_TO_START', {object: this.translate.instant(this.PATTERN_TRANSLATION_KEY, {count: 1}).toLowerCase()}),
            actions: this.NO_OVERLAY_ACTIONS
          } as NoDataOverlayComponentParams)
          .withOnGridReady((event: GridReadyEvent) => (this.gridApi = event.api));

        if (this.isLoadingSchedule || this.patterns.length > 0) {
          builder.withServerSideDataSource(this.SLICE_SIZE, this.getServerSideDataSource());
        }

        return builder.build();
      })
      .build();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('isLoadingSchedule' in changes && !changes.isLoadingSchedule.currentValue) {
      this.gridApi?.refreshServerSide({purge: true});
    }
  }

  private getColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('date')
        .withHeaderComponent(
          JumpToDatePickerHeaderComponent,
          (params: IHeaderParams) =>
            ({
              selectedDate: this.selectedDate,
              onSelectedDateChanged: (date: DateTime) => {
                if (this.patterns.length > 0 && !date.hasSame(this.selectedDate, 'day')) {
                  this.selectedDate = date;
                  const rowIndex = date.diff(this.PREVIEW_START_DATE).as('days');
                  const rowCount = rowIndex + this.SLICE_SIZE;
                  if (rowCount > params.api.getDisplayedRowCount()) {
                    params.api.setRowCount(rowCount);
                  }
                  params.api.ensureIndexVisible(rowIndex, 'top');
                }
              }
            }) as JumpToDatePickerHeaderParams
        )
        .withValueGetter((params: ValueGetterParams) => this.dateTimePipe.transform(params.data.date, DateTimeFormat.DATE_SHORT))
        .withTooltipValueGetter((params: ITooltipParams) => (!params.data.isWorkingDay ? this.translate.instant('ANGULAR_COMPONENT_LIBRARY.GENERAL.NON_WORKING_DAY') : null))
        .withMinWidth(this.DATE_COLUMN_WIDTH)
        .withMaxWidth(this.DATE_COLUMN_WIDTH)
        .withResizable(false)
        .withoutFilter()
        .withSuppressMovable('left')
        .withSuppressHeaderMenuButton()
        .build(),
      this.calendarTimelineService.getColumnDef(
        this.colDefBuilderFactoryService,
        this.unSubscribeOnViewDestroy,
        (rowNode: IRowNode, shifts: PatternShift[]) => this.addShift(rowNode, shifts),
        null,
        (scheduleItem: ScheduleItem, date: DateTime) => this.deleteShift(scheduleItem, date),
        null,
        Duration.fromObject({hours: 0})
      ),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('isWorkingDay')
        .withHeaderName('')
        .withCellRendererSelector((params: ICellRendererParams): CellRendererSelectorResult => {
          if (params.data.date < this.TODAY.plus({days: 1}) || params.data.isHoliday) {
            return {component: EmptyCellRendererComponent};
          }
          return {
            component: CellButtonComponent,
            params: {
              buttons: [
                {
                  icon: params?.value ? 'solid-do-not-disturb-on' : 'solid-do-not-disturb-off',
                  title: params?.value ? 'ANGULAR_COMPONENT_LIBRARY.GENERAL.MARK_AS_NON_WORKING_DAY' : 'ANGULAR_COMPONENT_LIBRARY.GENERAL.MARK_AS_WORKING_DAY',
                  onClick: (): void => {
                    params.data.scheduleItems.forEach((scheduleItem: ScheduleItem) => this.deleteShift(scheduleItem, params.data.date));
                    params.node.setDataValue('isWorkingDay', !params.value);
                    params.node.setDataValue('scheduleItems', []);

                    const unavailableDayIndex = this.unavailableDays.findIndex((unavailableDay: ScheduleUnavailableDay) => unavailableDay.date.hasSame(params.data.date, 'day'));
                    if (unavailableDayIndex === -1) {
                      this.unavailableDays.push(new ScheduleUnavailableDay(null, params.data.date));
                    } else {
                      this.unavailableDays.splice(unavailableDayIndex, 1);
                    }
                  }
                } as CrudOverviewButtonConfig
              ]
            } as CellButtonParams
          };
        })
        .withMaxWidth(this.CELL_BUTTON_COLUMN_WIDTH)
        .withResizable(false)
        .withoutFilter()
        .withSuppressMovable('right')
        .withSuppressHeaderMenuButton()
        .build()
    ];
  }

  private addShift(rowNode: IRowNode, shifts: PatternShift[]): void {
    if (!AssertionUtils.isNullOrUndefined(shifts)) {
      this.exceptions.push(new ScheduleException(null, shifts[0].shift, rowNode.data.date, shifts[0].startTime, shifts[0].endTime, shifts[0].breakTimes));

      rowNode.data.scheduleItems
        .filter((scheduleItem: ScheduleItem) => scheduleItem.name === shifts[0].shift.name)
        .forEach((scheduleItem: ScheduleItem, index: number) => {
          const foundException = this.exceptions.find((exception: ScheduleException) => exception.date.hasSame(rowNode.data.date, 'day') && exception.startTime?.equals(scheduleItem.startTime));
          if (foundException) {
            foundException.occurrenceOfShift = index + 1;
          }
        });
    }
  }

  private deleteShift(scheduleItem: ScheduleItem, date: DateTime): void {
    const foundExceptionIndex = this.exceptions.findIndex((exception: ScheduleException) => exception.date.hasSame(date, 'day') && exception.startTime?.equals(scheduleItem.startTime));
    if (foundExceptionIndex === -1) {
      const shiftToBeDeleted = this.schedulePreviews
        .find((schedulePreview: SchedulePreview) => schedulePreview.date.hasSame(date, 'day'))
        .shifts.find((shift: PatternShift) => shift.id === scheduleItem.id);
      this.exceptions.push(new ScheduleException(null, null, shiftToBeDeleted.isOvernight ? date.minus({days: 1}) : date, null, null, [], shiftToBeDeleted.originalSequence));
    } else {
      this.exceptions.splice(foundExceptionIndex, 1);
    }

    this.exceptions
      .filter((exception: ScheduleException) => exception.shift?.name === scheduleItem.name)
      .forEach((exception: ScheduleException) => {
        if (!AssertionUtils.isNullOrUndefined(exception.startTime) && exception.startTime > scheduleItem.startTime) {
          exception.occurrenceOfShift--;
        }
      });
  }

  private getServerSideDataSource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams): void => {
        if (this.isLoadingSchedule) {
          return;
        }

        const startRow = params.request.startRow;
        const endRow = params.request.endRow;

        this.schedules
          .getPreview(
            this.PREVIEW_START_DATE.plus({days: startRow}),
            this.PREVIEW_START_DATE.plus({days: endRow - 1}),
            new Schedule(undefined, 'preview', this.patterns, this.unavailableDays, this.exceptions, [])
          )
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe({
            next: (schedulePreviews: SchedulePreview[]) => {
              this.schedulePreviews.push(...schedulePreviews);
              params.success({rowData: this.getSchedulePreviewRowData(schedulePreviews)});
              params.api.refreshHeader();

              this.loadingForTheFirstTime = false;
              params.api.setGridOption('loadingCellRendererParams', {loadingForTheFirstTime: false});
            },
            error: () => {
              params.success({rowData: [], rowCount: 0});
            }
          });
      }
    };
  }

  private getSchedulePreviewRowData(schedulePreviews: SchedulePreview[]): SchedulePreviewRowData[] {
    const rowData = schedulePreviews.map(
      (data: SchedulePreview) =>
        ({
          date: data.date,
          isHoliday: data.isHoliday,
          isWorkingDay: data.isWorkingDay,
          scheduleItems: data.shifts.map((shift: PatternShift) => this.calendarTimelineService.getScheduleItemFromShift(shift))
        }) as SchedulePreviewRowData
    );
    this.calendarTimelineService.updateTotalHours(rowData, false);
    return rowData;
  }

  private subscribeToOverlayActions(): void {
    this.overlayActionsService.actionTriggeredEmitter.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((actionKey: string) => {
      if (actionKey === this.NO_OVERLAY_ACTIONS[0].key) {
        this.addPattern.emit();
      }
    });
  }
}
